import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Card,
  Typography,
  DatePicker,
  Select,
  Button,
  Spin,
  Table,
  Statistic,
  Space,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { fetchStatistics } from '../../store/slices/adminSlice';
import ErrorMessage from '../common/ErrorMessage';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const Statistics = () => {
  const dispatch = useDispatch();
  const { statistics, loading, error } = useSelector((state) => state.admin);
  const [filters, setFilters] = useState({
    start_date: null,
    end_date: null,
    region_id: undefined,
    seller_id: undefined,
  });

  const regions = useSelector((state) => state.ui.regions || []);

  useEffect(() => {
    dispatch(fetchStatistics({}));
  }, [dispatch]);

  const handleApplyFilters = () => {
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date.format('YYYY-MM-DD');
    if (filters.end_date) params.end_date = filters.end_date.format('YYYY-MM-DD');
    if (filters.region_id) params.region_id = filters.region_id;
    if (filters.seller_id) params.seller_id = filters.seller_id;

    dispatch(fetchStatistics(params));
  };

  const handleReset = () => {
    setFilters({
      start_date: null,
      end_date: null,
      region_id: undefined,
      seller_id: undefined,
    });
    dispatch(fetchStatistics({}));
  };

  if (loading && !statistics.totalOrders) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Yuklanmoqda..." />
      </div>
    );
  }

  const orderStatusLabels = {
    pending: 'Kutilmoqda',
    confirmed: 'Tasdiqlangan',
    processing: 'Jarayonda',
    shipped: 'Yetkazilmoqda',
    delivered: 'Yetkazildi',
    cancelled: 'Bekor qilindi',
    completed: 'Bajarildi'
  };

  const orderStatusColumns = [
    {
      title: 'Holati',
      dataIndex: 'status',
      key: 'status',
      render: (text) => <span>{orderStatusLabels[text] || text}</span>,
    },
    {
      title: 'Soni',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
    },
  ];

  const orderByRegionColumns = [
    {
      title: 'Region',
      dataIndex: 'region_name',
      key: 'region_name',
    },
    {
      title: 'Soni',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
    },
  ];

  const topProductsColumns = [
    {
      title: 'Mahsulot',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (text) => <Typography.Text ellipsis style={{ maxWidth: 200 }}>{text}</Typography.Text>,
    },
    {
      title: 'Sotildi',
      dataIndex: 'total_sold',
      key: 'total_sold',
      align: 'right',
    },
  ];

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>
        Statistika va tahlil
      </Title>

      {error && <ErrorMessage message={error} />}

      {/* Filters */}
      <Card title="Filtrlar" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Boshlanish sanasi"
              value={filters.start_date}
              onChange={(date) => setFilters(prev => ({ ...prev, start_date: date }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Tugash sanasi"
              value={filters.end_date}
              onChange={(date) => setFilters(prev => ({ ...prev, end_date: date }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Regionni tanlang"
              value={filters.region_id}
              onChange={(value) => setFilters(prev => ({ ...prev, region_id: value }))}
              allowClear
            >
              {regions.map((region) => (
                <Option key={region.id} value={region.id}>
                  {region.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space style={{ width: '100%', justifyContent: 'flex-start' }}>
              <Button type="primary" onClick={handleApplyFilters}>
                Filtrlash
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Tozalash
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Jami buyurtmalar"
              value={statistics.totalOrders || 0}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <Statistic
              title="Jami tushum"
              value={statistics.totalRevenue || 0}
              suffix="UZS"
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Orders by Status */}
        <Col xs={24} md={8}>
          <Card title="Buyurtmalar holati bo'yicha" bordered={false}>
            <Table
              columns={orderStatusColumns}
              dataSource={statistics.ordersByStatus}
              rowKey="status"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Ma\'lumot yo\'q' }}
            />
          </Card>
        </Col>

        {/* Orders by Region */}
        <Col xs={24} md={8}>
          <Card title="Buyurtmalar hududlar bo'yicha" bordered={false}>
            <Table
              columns={orderByRegionColumns}
              dataSource={statistics.ordersByRegion}
              rowKey="region_id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Ma\'lumot yo\'q' }}
            />
          </Card>
        </Col>

        {/* Top Products */}
        <Col xs={24} md={8}>
          <Card title="Eng ko'p sotilgan mahsulotlar" bordered={false}>
            <Table
              columns={topProductsColumns}
              dataSource={statistics.topProducts}
              rowKey="product_id"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Ma\'lumot yo\'q' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Statistics;
